import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Plus, Settings, ArrowLeft, Pencil, Check, X, Trash2, Filter, FileText, Archive, CheckCircle2, ArrowRight, Tag as TagIcon, Search, CalendarRange } from 'lucide-react';
import UserAvatar from './UserAvatar';
import TaskCard from './TaskCard';
import TaskModal from './TaskModal';
import CreateTaskModal from './CreateTaskModal';
import ProjectSettings from './ProjectSettings';
import BoardFilters from './BoardFilters';
import CompletedTasks from './CompletedTasks';
import TagManager from './TagManager';
import { resolveTags } from './tagPalette';
import { placementsByTask } from './horizonPlan';
import { formatWeeks } from './horizonTime';
import { taskMatchesQuery } from './bstMatch';
import { celebrate, popElement } from './Celebration';
import './KanbanBoard.css';
import { mediaUrl } from '../media';

const COLUMN_COLORS = ['#6366F1','#3B82F6','#F59E0B','#10B981','#8B5CF6','#EC4899','#EF4444','#06B6D4','#84CC16','#F97316'];

const DEFAULT_COLUMNS = [
  { id: 'backlog',    name: 'Backlog',     color: '#6366F1', order: 0 },
  { id: 'inprogress', name: 'In Progress', color: '#F59E0B', order: 1 },
  { id: 'done',       name: 'Done',        color: '#10B981', order: 2 }
];

const resolveColumns = (project) =>
  project?.columns?.length > 0 ? project.columns : DEFAULT_COLUMNS;

// Columns are user-defined and `status` is a separate three-value field, so
// there is no structural "done column". This recognises one by the id the
// default board ships with, or by a name that plainly means finished — which
// is what lets a drop into it count as completing the task (WR-009).
const DONE_COLUMN_NAME = /\b(done|complete|completed|finished|shipped|closed)\b/i;

// Newest-completed first. Tasks with no stamp (finished before completedAt
// existed, not yet backfilled by the archiver) sort last rather than jumping
// to the top.
const byCompletionDesc = (a, b) => {
  const ta = a.completedAt ? new Date(a.completedAt).getTime() : 0;
  const tb = b.completedAt ? new Date(b.completedAt).getTime() : 0;
  return tb - ta;
};

// Fallback celebration origin: the top of the column the card landed in,
// which is where a newly-completed card sorts to. Used only if the card
// element cannot be found.
const columnOrigin = (colId) => {
  const col = document.querySelector(`[data-rbd-droppable-id="${colId}"]`);
  if (!col) return undefined;
  const r = col.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + 60 };
};

const isDoneColumn = (colId, columns) => {
  const col = columns.find(c => c.id === colId);
  if (!col) return colId === 'done';
  return col.id === 'done' || DONE_COLUMN_NAME.test(col.name);
};

function KanbanBoard({ user, onUpdateProject, onDeleteProject }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject]           = useState(null);
  const [tasks, setTasks]               = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [filters, setFilters] = useState({ assignees: [], priorities: [], statuses: [], tags: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showTagManager, setShowTagManager] = useState(false);

  // Free-text board search (WR-013 §4). Matches title, description and the
  // BST id — 'BST-42', 'bst-0042' and a bare '42' all find BST-0042.
  const [search, setSearch] = useState('');

  // Per-tag task counts, including archived tasks. Only the manager needs
  // them — a delete confirm has to name every task it will touch, and most
  // of those may not be on the board at all.
  const [tagCounts, setTagCounts] = useState({});

  // True per-column counts of unarchived done tasks, from the server.
  // The board only carries the most recent slice of done tasks, so these
  // cannot be derived from `tasks` — a column showing 25 cards may hold 247.
  const [doneCounts, setDoneCounts] = useState({});
  // WR-024 B4: Horizons of this project, so a card can show its window.
  const [horizonList, setHorizonList] = useState([]);
  const [loading, setLoading]           = useState(true);

  // Deep-link support (?task=<id>&focus=comments). Notifications and pasted
  // URLs land here; see the effect below.
  const [deepLinkFocus, setDeepLinkFocus] = useState(null);
  const [deepLinkError, setDeepLinkError] = useState(null);

  // Rename state
  const [editingColId, setEditingColId] = useState(null);
  const [editingName, setEditingName]   = useState('');
  const renameInputRef = useRef(null);

  // Add column state
  const [showAddCol, setShowAddCol]   = useState(false);
  const [newColName, setNewColName]   = useState('');
  const [newColColor, setNewColColor] = useState(COLUMN_COLORS[0]);
  const addInputRef = useRef(null);

  // task id -> its placements on this project's Horizons (WR-024 B4).
  const placements = useMemo(() => placementsByTask(horizonList), [horizonList]);

  /** The card glyph for a task: its weeks, or 'H' when it is on a Horizon but unscheduled. */
  const horizonGlyph = (task) => {
    const p = (placements.get(String(task._id)) || [])[0];
    if (!p) return {};
    const weeks = formatWeeks(p.start, p.end);
    return {
      horizonWeeks: weeks || 'H',
      horizonTitle: `On Horizon "${p.horizonName}"${weeks ? ` · ${weeks}` : ' · not scheduled yet'}`,
    };
  };

  useEffect(() => { if (projectId) fetchData(); }, [projectId]);

  // ── Deep-link: open a specific task from ?task=<id> ────────────────
  //
  // Runs once the board's tasks are loaded (a notification click can land
  // here before the fetch resolves). The param is stripped with replace:true
  // so a refresh doesn't reopen the modal and Back doesn't bounce between
  // the linked and un-linked URL.
  //
  // Clearing the param is also what stops this from re-firing: the next run
  // sees no `task` and bails. Deliberately NOT keeping a set of handled ids —
  // that would block opening the same task again from a second notification.
  useEffect(() => {
    const taskId = searchParams.get('task');
    if (!taskId || loading) return;

    const target = tasks.find(t => t._id === taskId);
    if (target) {
      setDeepLinkFocus(searchParams.get('focus'));
      setSelectedTask(target);
    } else {
      // Deleted, moved to another project, or no longer visible. Stay on
      // the board — a stale notification must never break the page.
      setDeepLinkError('That task is no longer available. It may have been deleted or moved.');
    }

    const next = new URLSearchParams(searchParams);
    next.delete('task');
    next.delete('focus');
    setSearchParams(next, { replace: true });
  }, [searchParams, tasks, loading, setSearchParams]);

  // Auto-dismiss the not-found notice.
  useEffect(() => {
    if (!deepLinkError) return;
    const t = setTimeout(() => setDeepLinkError(null), 6000);
    return () => clearTimeout(t);
  }, [deepLinkError]);
  useEffect(() => { if (editingColId && renameInputRef.current) { renameInputRef.current.focus(); renameInputRef.current.select(); } }, [editingColId]);
  useEffect(() => { if (showAddCol && addInputRef.current) addInputRef.current.focus(); }, [showAddCol]);

  const fetchData = async () => {
    try {
      const [pRes, tRes, hRes] = await Promise.all([
        axios.get(`/projects/${projectId}`),
        axios.get(`/tasks/project/${projectId}`),
        // A failing Horizon list must never keep the board from rendering.
        axios.get(`/horizons/project/${projectId}`).catch(() => ({ data: [] }))
      ]);
      setProject(pRes.data);
      // The board endpoint returns { tasks, doneByColumn, … } rather than a
      // bare array since WR-008 capped the done slice.
      setTasks(tRes.data.tasks || []);
      setDoneCounts(tRes.data.doneByColumn || {});
      setHorizonList(hRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // ── Tags (WR-010) ─────────────────────────────────────────────────
  //
  // The project document is the single copy of the tag list. Every mutation
  // writes back into it, which is why a rename or recolour needs to touch no
  // task at all: cards and chips resolve ids against `project.tags`.

  const refreshTagCounts = async () => {
    try {
      const res = await axios.get(`/projects/${projectId}/tags`);
      setTagCounts(res.data.counts || {});
      // The server owns the tag list; take its copy so a change made in
      // another tab shows up when the manager is opened.
      setProject(p => (p ? { ...p, tags: res.data.tags || [] } : p));
    } catch (e) { console.error('Failed to load tag usage:', e); }
  };

  const handleCreateTag = async (name, color) => {
    const res = await axios.post(`/projects/${projectId}/tags`, { name, color });
    setProject(p => ({ ...p, tags: [...(p.tags || []), res.data] }));
    return res.data;
  };

  const handleUpdateTag = async (tagId, patch) => {
    const res = await axios.put(`/projects/${projectId}/tags/${tagId}`, patch);
    setProject(p => ({
      ...p,
      tags: (p.tags || []).map(t => (t._id === tagId ? res.data : t)),
    }));
    return res.data;
  };

  const handleDeleteTag = async (tagId) => {
    await axios.delete(`/projects/${projectId}/tags/${tagId}`);
    setProject(p => ({ ...p, tags: (p.tags || []).filter(t => t._id !== tagId) }));
    // The server pulled the id from every task in the project, archived ones
    // included; mirror that on the board's copy so no chip lingers.
    setTasks(prev => prev.map(t => (
      t.tags?.some(id => String(id) === String(tagId))
        ? { ...t, tags: t.tags.filter(id => String(id) !== String(tagId)) }
        : t
    )));
    setTagCounts(prev => {
      const next = { ...prev };
      delete next[String(tagId)];
      return next;
    });
    setFilters(f => ({ ...f, tags: f.tags.filter(id => String(id) !== String(tagId)) }));
  };

  const openTagManager = async () => {
    setShowTagManager(true);
    await refreshTagCounts();
  };

  /**
   * Keep the true done counts in step with a change to one task, so the Done
   * column header and the "Archive all done" confirm stay honest without a
   * refetch. Pass null for `before` on a task arriving, null for `after` on
   * one leaving (deleted or archived).
   *
   * Counted the same way the server counts: done AND not archived.
   */
  const applyDoneDelta = (before, after) => {
    const wasDone = !!before && before.status === 'done' && !before.archived;
    const isDone  = !!after  && after.status  === 'done' && !after.archived;
    if (!wasDone && !isDone) return;

    setDoneCounts(prev => {
      const next = { ...prev };
      if (wasDone) next[before.columnId] = Math.max(0, (next[before.columnId] || 0) - 1);
      if (isDone)  next[after.columnId]  = (next[after.columnId] || 0) + 1;
      return next;
    });
  };

  // ── Persist columns ───────────────────────────────────────────
  const persistColumns = async (updatedCols) => {
    setProject(p => ({ ...p, columns: updatedCols }));
    try { await onUpdateProject(project._id, { columns: updatedCols }); }
    catch { setProject(p => ({ ...p, columns: project.columns })); }
  };

  // ── Drag & drop ───────────────────────────────────────────────
  //
  // A drop used to move `columnId` alone, leaving `status` untouched — so
  // dragging a card into Done did not actually finish it, and the two could
  // drift apart indefinitely. A drop into a done column now carries the
  // status with it, and a drop back out reopens the task as In Progress.
  //
  // The update stays optimistic, matching how the board already worked, but
  // the celebration deliberately does not: it fires only once the server has
  // accepted the change, so a failed request never throws confetti.
  const handleDragEnd = async ({ source, destination, draggableId }) => {
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const newColId = destination.droppableId;
    const previous = tasks;
    const moved = tasks.find(t => t._id === draggableId);
    const columns = resolveColumns(project);

    const patch = { columnId: newColId };
    const wasDone = moved?.status === 'done';
    const nowDone = isDoneColumn(newColId, columns);
    if (nowDone && !wasDone)      patch.status = 'done';
    else if (!nowDone && wasDone) patch.status = 'inprogress';

    // Shown optimistically but never sent: the server owns completedAt. The
    // Done column sorts on it, so without this the card would sink to the
    // bottom for one round trip instead of appearing at the top.
    const optimistic = { ...patch };
    if (patch.status === 'done')     optimistic.completedAt = new Date().toISOString();
    else if (patch.status)           optimistic.completedAt = null;

    setTasks(prev => prev.map(t => t._id === draggableId ? { ...t, ...optimistic } : t));

    try {
      const res = await axios.put(`/tasks/${draggableId}`, patch);
      setTasks(prev => prev.map(t => t._id === draggableId ? res.data : t));
      applyDoneDelta(moved, res.data);

      if (patch.status === 'done') {
        // Two frames, not one. The card does not merely change column — a
        // done column sorts on completion, so it jumps to the top — and this
        // state update lands in a promise, where React schedules the commit
        // through its own scheduler. One rAF can win that race and hand back
        // the card's old position, which on a 25-card column is far off.
        // Two frames put the read safely after the commit and the paint.
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const el = document.querySelector(`[data-rbd-draggable-id="${draggableId}"]`);
          celebrate(el ? el.getBoundingClientRect() : columnOrigin(newColId));
          popElement(el);
        }));
      }
    } catch {
      setTasks(previous);
    }
  };

  // ── Rename column ─────────────────────────────────────────────
  const startRename = (col) => { setEditingColId(col.id); setEditingName(col.name); };
  const cancelRename = () => { setEditingColId(null); setEditingName(''); };
  const saveRename = async () => {
    if (!editingName.trim()) return cancelRename();
    const updated = project.columns.map(c => c.id === editingColId ? { ...c, name: editingName.trim() } : c);
    setEditingColId(null);
    await persistColumns(updated);
  };
  const renameKeyDown = (e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') cancelRename(); };

  // ── Add column ────────────────────────────────────────────────
  const handleAddColumn = async () => {
    if (!newColName.trim()) return;
    const id = newColName.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now();
    const updated = [...project.columns, { id, name: newColName.trim(), color: newColColor, order: project.columns.length }];
    setShowAddCol(false);
    setNewColName('');
    await persistColumns(updated);
  };
  const addKeyDown = (e) => { if (e.key === 'Enter') handleAddColumn(); if (e.key === 'Escape') { setShowAddCol(false); setNewColName(''); } };

  // ── Delete column ─────────────────────────────────────────────
  const handleDeleteColumn = async (colId) => {
    const count = tasks.filter(t => t.columnId === colId).length;
    const msg = count > 0
      ? `This column has ${count} task(s). They'll stay in the database but won't appear on the board. Delete anyway?`
      : 'Delete this column?';
    if (!window.confirm(msg)) return;
    await persistColumns(project.columns.filter(c => c.id !== colId));
  };

  // ── Task operations ───────────────────────────────────────────
  const handleCreateTask = async (taskData) => {
    const res = await axios.post('/tasks', { ...taskData, project: projectId });
    setTasks(prev => [...prev, res.data]);
    return res.data;
  };
  const handleUpdateTask = async (taskId, data) => {
    const before = tasks.find(t => t._id === taskId);
    const res = await axios.put(`/tasks/${taskId}`, data);
    setTasks(prev => prev.map(t => t._id === taskId ? res.data : t));
    applyDoneDelta(before, res.data);
    return res.data;
  };
  const handleDeleteTask = async (taskId) => {
    const before = tasks.find(t => t._id === taskId);
    await axios.delete(`/tasks/${taskId}`);
    setTasks(prev => prev.filter(t => t._id !== taskId));
    applyDoneDelta(before, null);
    setSelectedTask(null);
  };

  // ── Archive (WR-008) ──────────────────────────────────────────
  // Archiving hides finished work from the board; nothing is deleted and
  // everything here is reversible from the Completed view.

  const handleArchiveTask = async (taskId) => {
    const before = tasks.find(t => t._id === taskId);
    const res = await axios.put(`/tasks/${taskId}/archive`);
    setTasks(prev => prev.filter(t => t._id !== taskId));
    applyDoneDelta(before, null);
    setSelectedTask(null);
    return res.data;
  };

  // Scoped to one column. Columns and status are independent here, so this
  // archives the done tasks sitting in that column — not the column itself,
  // and not done tasks that happen to live elsewhere on the board.
  //
  // The count comes from the server's per-column total, NOT from the cards on
  // screen: a truncated Done column shows 25 of 247, and the confirm dialog
  // has to name the number that will actually be archived.
  const handleArchiveColumnDone = async (col) => {
    const count = doneCounts[col.id] || 0;
    if (count === 0) return;

    const noun = count === 1 ? 'done task' : 'done tasks';
    const ok = window.confirm(
      `Archive ${count} ${noun}?\n\n` +
      'They leave the board but are kept, and can be restored any time from Completed.'
    );
    if (!ok) return;

    try {
      const res = await axios.post(`/tasks/project/${projectId}/archive-done`, { columnId: col.id });
      const archived = new Set((res.data.taskIds || []).map(String));
      setTasks(prev => prev.filter(t => !archived.has(String(t._id))));
      setDoneCounts(prev => ({ ...prev, [col.id]: 0 }));
    } catch (e) { console.error('Failed to archive done tasks:', e); }
  };

  // A task restored from the Completed view rejoins the board. It may land
  // beyond the done slice the board is carrying, but showing it immediately
  // is what the user just asked for; the next load re-truncates.
  const handleTaskRestored = (task) => {
    setTasks(prev => (prev.some(t => t._id === task._id) ? prev : [...prev, task]));
    applyDoneDelta(null, task);
  };

  // A task archived from the Completed view leaves the board if it was on it.
  const handleTaskArchivedElsewhere = (task) => {
    const before = tasks.find(t => t._id === task._id);
    setTasks(prev => prev.filter(t => t._id !== task._id));
    if (before) applyDoneDelta(before, null);
  };


  if (loading) return <div className="loading-screen"><div className="loading-spinner"></div></div>;

  // Filter tasks based on active filters
  const getFilteredTasks = (tasks) => {
    if (!tasks) return [];
    
    return tasks.filter(task => {
      // Assignee filter
      if (filters.assignees.length > 0) {
        const taskAssigneeIds = task.assignedTo?.map(u => u._id) || [];
        const hasMatchingAssignee = filters.assignees.some(id => taskAssigneeIds.includes(id));
        if (!hasMatchingAssignee) return false;
      }
      
      // Priority filter
      if (filters.priorities.length > 0 && !filters.priorities.includes(task.priority)) {
        return false;
      }
      
      // Status filter
      if (filters.statuses.length > 0 && !filters.statuses.includes(task.status)) {
        return false;
      }

      // Free-text search (WR-013 §4), including the BST id.
      if (!taskMatchesQuery(task, search)) return false;

      // Tag filter (WR-010) — OR across the selected tags: a task shows if
      // it carries ANY of them. AND would make picking a second tag almost
      // always empty the board, which is not what a label filter is for.
      if (filters.tags.length > 0) {
        const taskTagIds = (task.tags || []).map(String);
        if (!filters.tags.some(id => taskTagIds.includes(String(id)))) return false;
      }

      return true;
    });
  };

  if (!project) return <div className="kanban-container"><div className="error-state">Project not found</div></div>;

  const columns = resolveColumns(project);
  const activeFilterCount =
    filters.assignees.length + filters.priorities.length + filters.statuses.length + filters.tags.length;
  // A search narrows the board the same way a filter does, so the done-column
  // header must report what is actually shown rather than the true total.
  const hasSearch = search.trim().length > 0;
  const hasActiveFilters = activeFilterCount > 0 || hasSearch;

  return (
    <>
      <div className="kanban-container">
        {/* Stale deep-link notice — non-blocking, auto-dismisses. */}
        {deepLinkError && (
          <div className="board-notice" role="status">
            <span>{deepLinkError}</span>
            <button
              className="board-notice-close"
              onClick={() => setDeepLinkError(null)}
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* ── Header ── */}
        <div className="kanban-header">
          {/* WR-018 §1 — row 1: project identity.
              The title used to share one flex row with the search field and
              seven toolbar buttons, so below wide-desktop it was squeezed into
              a narrow column and wrapped a word per line. It now owns the row. */}
          <div className="kanban-header-identity">
            <Link to="/" className="btn btn-ghost kanban-back"><ArrowLeft size={18} /></Link>
            {project.image ? (
              <div
                className="project-header-image"
                style={{ backgroundImage: `url(${mediaUrl(project.image)})` }}
              />
            ) : (
              <div className="project-color-dot" style={{ background: project.color }} />
            )}
            <div className="kanban-header-titles">
              {/* title attribute: the full name stays reachable on hover once
                  the single line ellipsizes. */}
              <h1 title={project.name}>{project.name}</h1>
              {project.description && (
                <p className="project-subtitle" title={project.description}>{project.description}</p>
              )}
            </div>
            {project.members?.length > 0 && (
              <div className="avatar-group">
                {project.members.slice(0, 3).map((m, i) => (
                  <UserAvatar key={m._id} user={m} className="avatar" title={m.name} style={{ zIndex: project.members.length - i }} />
                ))}
                {project.members.length > 3 && <div className="avatar avatar-overflow">+{project.members.length - 3}</div>}
              </div>
            )}
          </div>

          {/* Row 2 — toolbar. Order is unchanged from the single-row header. */}
          <div className="kanban-header-toolbar">
            <div className="board-search">
              <Search size={15} className="board-search-icon" />
              <input
                type="text"
                className="board-search-input"
                placeholder="Search tasks or BST-0042..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                aria-label="Search tasks"
              />
              {search && (
                <button className="board-search-clear" onClick={() => setSearch('')} aria-label="Clear search">
                  <X size={14} />
                </button>
              )}
            </div>
            <button className="btn btn-ghost" onClick={() => setShowFilters(true)}>
              <Filter size={18} />
              Filters
              {activeFilterCount > 0 && (
                <span className="filter-badge">{activeFilterCount}</span>
              )}
            </button>
            <button className="btn btn-ghost" onClick={openTagManager}>
              <TagIcon size={18} />
              Tags
            </button>
            <button className="btn btn-ghost" onClick={() => navigate(`/project/${projectId}/folios`)}>
              <FileText size={18} />
              Folios
            </button>
            {/* WR-024 B1: the slot WR-022 freed. The route comes in a later task. */}
            <button className="btn btn-ghost" onClick={() => navigate(`/project/${projectId}/horizon`)}>
              <CalendarRange size={18} />
              Horizon
            </button>
            <button className="btn btn-ghost" onClick={() => setShowCompleted(true)}>
              <CheckCircle2 size={18} />
              Completed
            </button>
            <button className="btn btn-ghost" onClick={() => setShowSettings(true)}><Settings size={18} /> Settings</button>
          </div>
        </div>

        {/* ── Filter Modal ── */}
        {showFilters && (
          <BoardFilters 
            filters={filters} 
            onFilterChange={setFilters} 
            project={project}
            onClose={() => setShowFilters(false)}
          />
        )}

        {/* ── Board ── */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="kanban-board-scroll">
            <div className="kanban-board">
              {columns.map(col => {
                let colTasks = getFilteredTasks(tasks).filter(t => t.columnId === col.id);

                // The board carries only the most recent slice of done tasks,
                // so a done column sorts on completion rather than `order` —
                // that is what puts a just-finished card at the top and keeps
                // the visible slice meaningfully "the newest N".
                const isDoneCol = isDoneColumn(col.id, columns);
                if (isDoneCol) colTasks = [...colTasks].sort(byCompletionDesc);

                // True count from the server; the cards on screen may be a
                // fraction of it.
                const doneInCol   = doneCounts[col.id] || 0;
                const loadedDone  = tasks.filter(t => t.columnId === col.id && t.status === 'done').length;
                const truncated   = doneInCol > loadedDone;

                // With filters on, the board is explicitly a partial view, so
                // the header reports what is actually shown. With no filters
                // a done column reports the real total, which is the whole
                // point of `doneCounts`.
                const headerCount = (isDoneCol && !hasActiveFilters) ? doneInCol : colTasks.length;
                return (
                  <div key={col.id} className="kanban-column">
                    <div className="kanban-column-header">
                      <div className="kanban-column-title">
                        <div className="column-indicator" style={{ background: col.color }} />
                        {editingColId === col.id ? (
                          <div className="column-rename-input-group">
                            <input ref={renameInputRef} className="column-rename-input" value={editingName}
                              onChange={e => setEditingName(e.target.value)} onKeyDown={renameKeyDown} />
                            <button className="column-rename-btn confirm" onClick={saveRename}><Check size={14} /></button>
                            <button className="column-rename-btn cancel" onClick={cancelRename}><X size={14} /></button>
                          </div>
                        ) : (
                          <div className="column-name-display" onClick={() => startRename(col)} title="Click to rename">
                            <h3>{col.name}</h3>
                            <Pencil size={13} className="column-edit-icon" />
                          </div>
                        )}
                        <span
                          className="task-count"
                          title={truncated ? `Showing ${loadedDone} of ${doneInCol} completed tasks` : undefined}
                        >
                          {headerCount}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {doneInCol > 0 && (
                          <button
                            className="column-archive-btn"
                            onClick={() => handleArchiveColumnDone(col)}
                            title={`Archive ${doneInCol} done task${doneInCol === 1 ? '' : 's'} in this column`}
                          >
                            <Archive size={14} />
                            <span className="column-archive-count">{doneInCol}</span>
                          </button>
                        )}
                        <button className="column-add-btn" onClick={() => setShowCreateModal(col.id)} title="Add task"><Plus size={16} /></button>
                        <button className="column-delete-btn" onClick={() => handleDeleteColumn(col.id)} title="Delete column"><Trash2 size={14} /></button>
                      </div>
                    </div>

                    <Droppable droppableId={col.id}>
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}
                          className={`kanban-column-content ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}>
                          {colTasks.map((task, index) => (
                            <Draggable key={task._id} draggableId={task._id} index={index}>
                              {(provided, snapshot) => (
                                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                  onClick={() => setSelectedTask(task)}>
                                  <TaskCard
                                    task={task}
                                    tags={resolveTags(project, task.tags)}
                                    isDragging={snapshot.isDragging}
                                    {...horizonGlyph(task)}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          {truncated && (
                            <button
                              className="column-view-all"
                              onClick={() => setShowCompleted(true)}
                            >
                              View all {doneInCol} completed
                              <ArrowRight size={14} />
                            </button>
                          )}
                          {colTasks.length === 0 && !truncated && (
                            <div className="empty-column">
                              <p>No tasks yet</p>
                              <button className="btn btn-ghost btn-sm" onClick={() => setShowCreateModal(col.id)}>Add a task</button>
                            </div>
                          )}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}

              {/* ── Add Column card ── */}
              <div className="add-column-card">
                {showAddCol ? (
                  <div className="add-column-form">
                    <input ref={addInputRef} className="add-column-input" placeholder="Column name..."
                      value={newColName} onChange={e => setNewColName(e.target.value)} onKeyDown={addKeyDown} />
                    <div className="add-column-colors">
                      {COLUMN_COLORS.map(c => (
                        <button key={c} className={`color-swatch ${newColColor === c ? 'selected' : ''}`}
                          style={{ background: c }} onClick={() => setNewColColor(c)} />
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleAddColumn}>
                        <Check size={16} /> Add
                      </button>
                      <button className="btn btn-secondary" onClick={() => { setShowAddCol(false); setNewColName(''); }}>
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="add-column-btn" onClick={() => setShowAddCol(true)}>
                    <Plus size={20} /><span>Add column</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </DragDropContext>
      </div>

      {selectedTask && (
        <TaskModal task={selectedTask} project={project}
          focusSection={deepLinkFocus}
          onClose={() => {
            setSelectedTask(null);
            setDeepLinkFocus(null);
            axios.get(`/horizons/project/${projectId}`).then(res => setHorizonList(res.data || [])).catch(() => {});
          }}
          onUpdate={handleUpdateTask} onDelete={handleDeleteTask}
          onArchive={handleArchiveTask} onCreateTag={handleCreateTag} />
      )}
      {showTagManager && (
        <TagManager
          tags={project.tags || []}
          counts={tagCounts}
          onClose={() => setShowTagManager(false)}
          onCreate={handleCreateTag}
          onUpdate={handleUpdateTag}
          onDelete={handleDeleteTag} />
      )}
      {showCompleted && (
        <CompletedTasks project={project}
          onClose={() => setShowCompleted(false)}
          onRestored={handleTaskRestored}
          onArchived={handleTaskArchivedElsewhere} />
      )}
      {showCreateModal && (
        <CreateTaskModal status={showCreateModal} project={project}
          onClose={() => setShowCreateModal(null)} onCreate={handleCreateTask} />
      )}
      {showSettings && (
        <ProjectSettings project={project} onClose={() => setShowSettings(false)}
          onUpdate={onUpdateProject} onDelete={onDeleteProject} />
      )}
    </>
  );
}

export default KanbanBoard;
